import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Form } from '@anytag/shared-atoms';
import { useAddressQuery } from '@anytag/graphql';
import { TextField } from '@anytag/shared-molecules';
import { isJapanCountry } from '@anytag/shared-utils';
import { useDeepCompareEffect } from '@anytag/shared-hooks';
import { useAddressAndPaymentInfoPermissions, useUserRoles } from '@anytag/auth';
import { InfluencerFormValues } from '../../schema-types';
import { defaultInfluencerFormValues } from '../../utils';

export const AddressInformationSection = () => {
  const { t } = useTranslation();
  const { isAdminStaff } = useUserRoles();
  const { hidePaymentAndAddressWidgets, disableAddressEditing } = useAddressAndPaymentInfoPermissions();
  const {
    watch,
    setValue,
    formState: { defaultValues }
  } = useFormContext<InfluencerFormValues>();

  const [influencerId, countryId] = watch(['id', 'countryId']);

  const { data } = useAddressQuery({
    skip: !influencerId,
    variables: { influencerId: Number(influencerId) },
    onCompleted: ({ address }) => {
      if (address?.info) {
        setValue('addressInformation', address.info);
      }
    }
  });

  useDeepCompareEffect(() => {
    // When parent form reset to update new values, we need to sync current value into parent form as well
    setValue('addressInformation', data?.address?.info || defaultInfluencerFormValues.addressInformation);
  }, [defaultValues]);

  useEffect(() => {
    if (countryId !== 'JP') {
      setValue('addressInformation.furigana', null);
    }
  }, [countryId]);

  if (!(isAdminStaff && influencerId && !hidePaymentAndAddressWidgets)) return null;

  const isEditable = disableAddressEditing ? false : data?.address?.isEditable || false;

  return (
    <>
      <Form.SectionTitle className="mt-3 mb-0" label={t('Address Information')} />

      <Form.Fields itemsPerRow={2}>
        <TextField
          disabled={!isEditable}
          title={t('TextForm.Full name')}
          name="addressInformation.fullName"
          placeholder={t<string>('TextForm.Full name')}
        />

        {isJapanCountry(countryId) ? (
          <TextField
            disabled={!isEditable}
            title={t('TextForm.Furigana')}
            name="addressInformation.furigana"
            placeholder={t<string>('TextForm.Furigana')}
          />
        ) : null}

        <TextField
          type="number"
          disabled={!isEditable}
          placeholder="000000000000"
          title={t('TextForm.Postal code')}
          name="addressInformation.postalCode"
        />

        <TextField
          disabled={!isEditable}
          placeholder="Bangkok"
          title={t('TextForm.Province')}
          name="addressInformation.province"
        />

        <TextField
          disabled={!isEditable}
          placeholder="Watthana"
          title={t('TextForm.City')}
          name="addressInformation.city"
        />

        <TextField
          disabled={!isEditable}
          title={t('TextForm.Address')}
          name="addressInformation.address"
          placeholder="Khlong Tan Nuea 46 Sukhumvit Rd"
        />

        <TextField
          disabled={!isEditable}
          name="addressInformation.address2"
          placeholder="apartment AnyTag 101"
          title={t('TextForm.apartment, suite, etc')}
        />
      </Form.Fields>
    </>
  );
};
